<template>
    <v-container v-if="!isAdmin" fluid class="px-10 welcome">
        <v-img width="200px" contain :src="require('@/assets/main-logo.png')" />
        <div class="welcome-message">
            Welcome to Back in the game cms
        </div>
    </v-container>
    <v-container v-else fluid class="dashboard">
        <page-header title="Dashboard" icon="mdi-view-dashboard-outline">
            <template v-slot:actions>
                <v-btn small text :loading="loading" @click="fetchDashboard">
                    <v-icon left small>mdi-refresh</v-icon>Refresh
                </v-btn>
            </template>
        </page-header>

        <v-card class="dash-card dash-filters" elevation="1">
            <div class="dash-filters__row">
                <v-chip-group v-model="period" mandatory column active-class="dash-chip--active" class="dash-filters__periods">
                    <v-chip v-for="option in periodOptions" :key="option.value" :value="option.value" small outlined>
                        {{ option.value === 'season' && periodSeason ? `Season ${seasonLabel(periodSeason)}` : option.text }}
                    </v-chip>
                </v-chip-group>
                <v-spacer />
                <div class="dash-filters__group">
                    <span class="text-caption grey--text text--darken-1 mr-2">Group by</span>
                    <v-btn-toggle v-model="groupByChoice" mandatory dense color="primary darken-1">
                        <v-btn v-for="unit in groupUnits" :key="unit.value" :value="unit.value" small>
                            {{ unit.text }}
                        </v-btn>
                    </v-btn-toggle>
                </div>
            </div>
            <v-row v-if="period === 'custom'" dense class="mt-1">
                <v-col cols="12" sm="6" md="3">
                    <date-time-picker date-label="From" :date="customFrom" @setDate="customFrom = $event" />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <date-time-picker date-label="To" :date="customTo" @setDate="customTo = $event" />
                </v-col>
            </v-row>
            <v-row dense class="mt-2">
                <v-col cols="12" sm="6" md="3">
                    <v-autocomplete
                        v-model="filters.seasonId"
                        outlined
                        dense
                        hide-details
                        clearable
                        :items="seasons"
                        :item-text="seasonLabel"
                        item-value="id"
                        label="Season"
                        placeholder="All seasons"
                        persistent-placeholder
                        prepend-inner-icon="mdi-calendar"
                    />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-autocomplete
                        v-model="filters.leagueId"
                        outlined
                        dense
                        hide-details
                        clearable
                        :items="leagues"
                        item-text="title"
                        item-value="id"
                        label="League"
                        placeholder="All leagues"
                        persistent-placeholder
                        prepend-inner-icon="mdi-podium-gold"
                    />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-select
                        v-model="filters.competition"
                        outlined
                        dense
                        hide-details
                        :items="competitionOptions"
                        label="Competition"
                        prepend-inner-icon="mdi-trophy-outline"
                    />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-select
                        v-model="filters.phase"
                        outlined
                        dense
                        hide-details
                        :items="phaseOptions"
                        label="Phase"
                        prepend-inner-icon="mdi-stairs"
                    />
                </v-col>
            </v-row>
            <div class="dash-filters__range">
                <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>{{ rangeText }}
                <a v-if="hasFilters" class="ml-3" @click="clearFilters">Clear filters</a>
            </div>
        </v-card>

        <v-alert v-if="error" type="error" dense text class="mt-4 mb-0">
            {{ error }}
        </v-alert>

        <template v-if="data">
            <div v-if="financeNotes.length" class="dash-notes">
                <div v-for="note in financeNotes" :key="note" class="dash-note">
                    <v-icon small color="primary darken-1" class="mr-2">mdi-information-outline</v-icon>
                    <span>{{ note }}</span>
                </div>
            </div>

            <div class="dash-kpis">
                <div class="dash-kpi">
                    <div class="dash-kpi__label">
                        <v-icon small>mdi-basketball</v-icon>Matches
                    </div>
                    <div class="dash-kpi__value">
                        {{ totals.matches }}
                    </div>
                    <div class="dash-kpi__sub">
                        {{ totals.played }} played · {{ totals.upcoming }} upcoming
                    </div>
                </div>
                <div class="dash-kpi">
                    <div class="dash-kpi__label">
                        <v-icon small>mdi-chart-donut</v-icon>Competition
                    </div>
                    <div class="dash-split">
                        <div v-for="item in categoryItems" :key="item.key" class="dash-split__item">
                            <span class="bar-chart-dot" :style="{ background: item.color }" />
                            {{ item.label }} <b>{{ totals[item.key] }}</b>
                        </div>
                    </div>
                    <div class="dash-kpi__sub">
                        {{ totals.postponed }} postponed (not counted)
                    </div>
                </div>
                <div class="dash-kpi">
                    <div class="dash-kpi__label">
                        <v-icon small>mdi-trending-up</v-icon>Est. revenue
                    </div>
                    <div class="dash-kpi__value">
                        € {{ money(totals.revenue) }}
                    </div>
                    <div v-if="totals.subscriptionRevenue" class="dash-kpi__sub">
                        Matches € {{ money(totals.matchRevenue) }} · Subscriptions € {{ money(totals.subscriptionRevenue) }}
                    </div>
                    <div v-else class="dash-kpi__sub">
                        {{ totals.matches ? `€ ${money(totals.revenue / totals.matches)} per match` : '—' }}
                    </div>
                </div>
                <div class="dash-kpi">
                    <div class="dash-kpi__label">
                        <v-icon small>mdi-trending-down</v-icon>Est. cost
                    </div>
                    <div class="dash-kpi__value">
                        € {{ money(totals.cost) }}
                    </div>
                    <div class="dash-kpi__sub">
                        Arenas € {{ money(totals.venueCost) }} · Staff € {{ money(totals.matchStaffCost + totals.monthlyStaffCost) }}
                    </div>
                </div>
                <div class="dash-kpi">
                    <div class="dash-kpi__label">
                        <v-icon small>mdi-scale-balance</v-icon>Result
                    </div>
                    <div :class="['dash-kpi__value', totals.result >= 0 ? 'dash-positive' : 'dash-negative']">
                        € {{ money(totals.result) }}
                    </div>
                    <div class="dash-kpi__sub">
                        {{ totals.revenue ? `${Math.round((totals.result / totals.revenue) * 100)}% margin` : '—' }}
                    </div>
                </div>
            </div>

            <v-row class="mt-1">
                <v-col cols="12" lg="6">
                    <v-card class="dash-card" elevation="1">
                        <div class="dash-card__title">
                            Matches per {{ groupByLabel }}
                            <div class="dash-legend">
                                <span v-for="item in categoryItems" :key="item.key"><span class="bar-chart-dot" :style="{ background: item.color }" />{{ item.label }}</span>
                            </div>
                        </div>
                        <bar-chart
                            :labels="bucketLabels"
                            :tooltip-titles="bucketTitles"
                            :series="matchSeries"
                            :tooltip-extra="matchExtras"
                            stacked
                        />
                    </v-card>
                </v-col>
                <v-col cols="12" lg="6">
                    <v-card class="dash-card" elevation="1">
                        <div class="dash-card__title">
                            Finance per {{ groupByLabel }}
                            <div class="dash-legend">
                                <span><span class="bar-chart-dot" :style="{ background: colors.revenue }" />Revenue</span>
                                <span><span class="bar-chart-dot" :style="{ background: colors.cost }" />Cost</span>
                            </div>
                        </div>
                        <bar-chart
                            :labels="bucketLabels"
                            :tooltip-titles="bucketTitles"
                            :series="financeSeries"
                            :tooltip-extra="financeExtras"
                            :format-value="value => `€ ${money(value)}`"
                        />
                    </v-card>
                </v-col>
            </v-row>

            <!-- who has paid the subscription of the season (filtered on, or running today) -->
            <v-row v-if="data.subscriptions">
                <v-col cols="12">
                    <v-card class="dash-card" elevation="1">
                        <div class="dash-card__title">
                            Subscriptions · {{ data.subscriptions.season }}
                            <span class="dash-subs__collected">€ {{ money(data.subscriptions.collected) }} collected</span>
                        </div>
                        <div class="dash-subs">
                            <div class="dash-subs__summary">
                                <div class="dash-subs__count">
                                    <b>{{ data.subscriptions.paid }}</b> / {{ data.subscriptions.teams }} teams paid
                                </div>
                                <v-progress-linear
                                    :value="subscriptionPercent"
                                    height="8"
                                    rounded
                                    color="green darken-1"
                                    background-color="red lighten-4"
                                />
                                <div class="text-caption grey--text text--darken-1 mt-1">
                                    {{ subscriptionPercent }}% · {{ data.subscriptions.unpaid.length }} unpaid
                                </div>
                            </div>
                            <div class="dash-subs__unpaid">
                                <div class="dash-subs__label">Not paid yet</div>
                                <div v-if="!data.subscriptions.unpaid.length" class="dash-empty">
                                    Every team has paid.
                                </div>
                                <v-chip
                                    v-for="team in data.subscriptions.unpaid"
                                    :key="team.id"
                                    small
                                    outlined
                                    color="red darken-1"
                                    class="mr-1 mb-1"
                                    title="Open the team to mark it paid"
                                    @click="$router.push(`/teams/${team.id}/edit`)"
                                >
                                    {{ team.name }}
                                </v-chip>
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="6" xl="4">
                    <v-card class="dash-card" elevation="1">
                        <div class="dash-card__title">
                            By league
                        </div>
                        <breakdown-table :rows="data.leagues" :money="money" empty="No matches" />
                    </v-card>
                </v-col>
                <v-col cols="12" md="6" xl="4">
                    <v-card class="dash-card" elevation="1">
                        <div class="dash-card__title">
                            By arena
                        </div>
                        <breakdown-table :rows="data.arenas" :money="money" empty="No matches" />
                    </v-card>
                </v-col>
                <v-col cols="12" xl="4">
                    <v-card class="dash-card" elevation="1">
                        <div class="dash-card__title">
                            Upcoming matches
                        </div>
                        <div v-if="!data.upcoming.length" class="dash-empty">
                            No upcoming matches
                        </div>
                        <div
                            v-for="match in data.upcoming"
                            :key="match.id"
                            class="dash-upcoming"
                            @click="$router.push(`/matches/${match.id}/edit`)"
                        >
                            <div class="dash-upcoming__date">
                                <b>{{ formatDay(match.matchDatetime) }}</b>
                                <span>{{ formatTime(match.matchDatetime) }}</span>
                            </div>
                            <div class="dash-upcoming__teams">
                                <div>{{ match.team1 }} <span class="grey--text">vs</span> {{ match.team2 }}</div>
                                <div class="text-caption grey--text text--darken-1">
                                    <v-icon x-small>mdi-stadium-outline</v-icon> {{ match.arena || '—' }}<span v-if="match.cup"> · Cup</span>
                                </div>
                            </div>
                            <v-icon small color="grey">
                                mdi-chevron-right
                            </v-icon>
                        </div>
                        <div class="dash-counters">
                            <div><b>{{ data.activeTeams }}</b> active teams</div>
                            <div><b>{{ data.activePlayers }}</b> active players</div>
                            <div class="dash-counters__link" @click="$router.push('/team-applications')">
                                <b :class="{ 'orange--text text--darken-2': data.pendingApplications }">{{ data.pendingApplications }}</b> pending applications
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </template>
        <div v-else-if="loading" class="dash-loading">
            <v-progress-circular indeterminate color="primary" />
        </div>
    </v-container>
</template>

<script>
    import moment from 'moment-timezone'
    import PageHeader from '@/components/PageHeader'
    import BarChart from '@/components/BarChart'
    import DateTimePicker from '@/components/DateTimePicker'
    import BreakdownTable from '@/components/BreakdownTable'
    import dashboardService from '@/services/dashboard'
    import seasonService from '@/services/season'
    import leagueService from '@/services/league'
    import { seasonLabel, sortSeasons } from '@/utils/season'

    const ZONE = 'Europe/Athens'
    const STORAGE_KEY = 'commercial-cms-dashboard'

    export default {
        name: 'Home',
        components: { PageHeader, BarChart, DateTimePicker, BreakdownTable },
        data: () => ({
            loading: false,
            error: null,
            data: null,
            seasons: [],
            leagues: [],
            period: 'month',
            customFrom: null,
            customTo: null,
            // 'auto' picks day / week / month from the length of the period
            groupByChoice: 'auto',
            filters: { seasonId: null, leagueId: null, competition: null, phase: null },
            periodOptions: [
                { value: 'week', text: 'This week' },
                { value: 'month', text: 'This month' },
                { value: 'quarter', text: 'Last 3 months' },
                { value: 'season', text: 'Season' },
                { value: 'year', text: 'This year' },
                { value: 'custom', text: 'Custom' }
            ],
            groupUnits: [
                { value: 'auto', text: 'Auto' },
                { value: 'day', text: 'Day' },
                { value: 'week', text: 'Week' },
                { value: 'month', text: 'Month' }
            ],
            competitionOptions: [
                { value: null, text: 'League & cup' },
                { value: 'league', text: 'League only' },
                { value: 'cup', text: 'Cup only' }
            ],
            phaseOptions: [
                { value: null, text: 'All phases' },
                { value: 'placements', text: 'Placements' },
                { value: 'regular_season', text: 'Regular season' },
                { value: 'playoffs', text: 'Playoffs' }
            ],
            colors: { league: '#009688', cup: '#5C6BC0', playoffs: '#26A69A', revenue: '#43A047', cost: '#E57373' },
            requestId: 0
        }),
        computed: {
            isAdmin() {
                const roles = this.$store.getters.getUserRoles
                return !!roles && roles.includes('ROLE_ADMIN')
            },
            categoryItems() {
                return [
                    { key: 'leagueMatches', bucketKey: 'leagueMatches', label: 'League', color: this.colors.league },
                    { key: 'cupMatches', bucketKey: 'cupMatches', label: 'Cup', color: this.colors.cup },
                    { key: 'playoffMatches', bucketKey: 'playoffMatches', label: 'Playoffs', color: this.colors.playoffs }
                ]
            },
            // the season used by the "Season" period: the filtered one, else the one running today
            periodSeason() {
                if (this.filters.seasonId) return this.seasons.find(season => season.id === this.filters.seasonId) || null
                const now = moment()
                return this.seasons.find(season => moment(season.startDate).isSameOrBefore(now) && (!season.endDate || moment(season.endDate).isAfter(now))) ||
                    this.seasons[0] || null
            },
            // [from, to) in Athens time
            range() {
                const today = moment.tz(ZONE).startOf('day')
                switch (this.period) {
                case 'week':
                    return [today.clone().startOf('isoWeek'), today.clone().startOf('isoWeek').add(1, 'week')]
                case 'quarter':
                    return [today.clone().startOf('month').subtract(2, 'months'), today.clone().startOf('month').add(1, 'month')]
                case 'year':
                    return [today.clone().startOf('year'), today.clone().startOf('year').add(1, 'year')]
                case 'season': {
                    const season = this.periodSeason
                    if (!season) return null
                    const start = moment(season.startDate).tz(ZONE).startOf('day')
                    const end = season.endDate ? moment(season.endDate).tz(ZONE).startOf('day').add(1, 'day') : today.clone().add(1, 'day')
                    return [start, end]
                }
                case 'custom':
                    if (!this.customFrom || !this.customTo) return null
                    return [moment.tz(this.customFrom, ZONE).startOf('day'), moment.tz(this.customTo, ZONE).startOf('day').add(1, 'day')]
                default:
                    return [today.clone().startOf('month'), today.clone().startOf('month').add(1, 'month')]
                }
            },
            groupBy() {
                if (this.groupByChoice !== 'auto') return this.groupByChoice
                if (!this.range) return 'month'
                const days = this.range[1].diff(this.range[0], 'days')
                if (days <= 14) return 'day'
                if (days <= 120) return 'week'
                return 'month'
            },
            groupByLabel() {
                return this.groupBy
            },
            query() {
                if (!this.range || !this.range[0].isBefore(this.range[1])) return null
                return {
                    from: this.range[0].toISOString(),
                    to: this.range[1].toISOString(),
                    groupBy: this.groupBy,
                    seasonId: this.filters.seasonId,
                    leagueId: this.filters.leagueId,
                    competition: this.filters.competition,
                    phase: this.filters.phase
                }
            },
            rangeText() {
                if (!this.range) return this.period === 'custom' ? 'Pick both dates' : 'No season found'
                if (!this.range[0].isBefore(this.range[1])) return '"From" must be before "To"'
                const last = this.range[1].clone().subtract(1, 'day')
                return `${this.range[0].format('D MMM YYYY')} – ${last.format('D MMM YYYY')} · grouped by ${this.groupBy}`
            },
            hasFilters() {
                return Object.values(this.filters).some(value => value !== null && value !== undefined)
            },
            totals() {
                return this.data ? this.data.totals : {}
            },
            buckets() {
                return this.data ? this.data.buckets : []
            },
            bucketLabels() {
                const format = { day: 'ddd D/M', week: 'D/M', month: 'MMM YY' }[this.data.groupBy] || 'D/M'
                return this.buckets.map(bucket => moment(bucket.start).tz(ZONE).format(format))
            },
            bucketTitles() {
                return this.buckets.map(bucket => {
                    const start = moment(bucket.start).tz(ZONE)
                    if (this.data.groupBy === 'day') return start.format('dddd D MMM YYYY')
                    if (this.data.groupBy === 'month') return start.format('MMMM YYYY')
                    return `${start.format('D MMM')} – ${moment(bucket.end).tz(ZONE).subtract(1, 'day').format('D MMM YYYY')}`
                })
            },
            matchSeries() {
                return this.categoryItems.map(item => ({ label: item.label, color: item.color, values: this.buckets.map(bucket => bucket[item.bucketKey]) }))
            },
            matchExtras() {
                return this.buckets.map(bucket => {
                    const rows = [{ label: 'Total', value: bucket.matches }]
                    if (bucket.postponed) rows.push({ label: 'Postponed', value: bucket.postponed })
                    return rows
                })
            },
            subscriptionPercent() {
                const status = this.data && this.data.subscriptions
                return status && status.teams ? Math.round((status.paid / status.teams) * 100) : 0
            },
            financeSeries() {
                return [
                    { label: 'Revenue', color: this.colors.revenue, values: this.buckets.map(bucket => bucket.revenue) },
                    { label: 'Cost', color: this.colors.cost, values: this.buckets.map(bucket => bucket.cost) }
                ]
            },
            financeExtras() {
                return this.buckets.map(bucket => {
                    const rows = [{ label: 'Result', value: `€ ${this.money(bucket.revenue - bucket.cost)}` }]
                    if (bucket.subscriptionRevenue) rows.push({ label: 'incl. subscriptions', value: `€ ${this.money(bucket.subscriptionRevenue)}` })
                    if (bucket.monthlyStaffCost) rows.push({ label: 'incl. monthly salaries', value: `€ ${this.money(bucket.monthlyStaffCost)}` })
                    return rows
                })
            },
            financeNotes() {
                const notes = []
                const totals = this.totals
                if (totals.matchesWithoutRevenue) notes.push(`${totals.matchesWithoutRevenue} match(es) belong to a season without "Revenue per match", counted as € 0. Set it on the season form.`)
                if (totals.matchesWithoutCost) notes.push(`${totals.matchesWithoutCost} match(es) have no arena cost (no arena or default cost on their season), counted as € 0.`)
                if (totals.subscriptionsWithoutAmount) notes.push(`${totals.subscriptionsWithoutAmount} subscription(s) paid in the period have no amount, counted as € 0. Add it on the team form.`)
                if (totals.subscriptionRevenue && (this.filters.competition || this.filters.phase)) {
                    notes.push('Subscriptions are not split per competition or phase: they are included in full for the period.')
                }
                if (totals.monthlyStaffCost && (this.filters.leagueId || this.filters.competition || this.filters.phase)) {
                    notes.push('Monthly salaries are not split per league, competition or phase: they are included in full for the period.')
                }
                return notes
            }
        },
        watch: {
            query: {
                handler(value, old) {
                    if (JSON.stringify(value) === JSON.stringify(old)) return
                    this.saveState()
                    this.fetchDashboard()
                },
                deep: true
            }
        },
        created() {
            if (!this.isAdmin) return
            this.loadState()
            seasonService.getSeasons().then(resp => {
                this.seasons = sortSeasons(resp.data.content)
            })
            leagueService.getAllLeagues().then(resp => {
                this.leagues = resp.data
            })
            this.fetchDashboard()
        },
        methods: {
            seasonLabel,
            fetchDashboard() {
                if (!this.isAdmin || !this.query) return
                const id = ++this.requestId
                this.loading = true
                this.error = null
                dashboardService.getDashboard(this.query).then(resp => {
                    // a newer request was sent meanwhile
                    if (id !== this.requestId) return
                    this.data = resp.data
                }).catch(error => {
                    if (id !== this.requestId) return
                    this.error = (error.response && error.response.data && error.response.data.message) || 'Could not load the dashboard'
                }).finally(() => {
                    if (id === this.requestId) this.loading = false
                })
            },
            clearFilters() {
                this.filters = { seasonId: null, leagueId: null, competition: null, phase: null }
            },
            money(value) {
                return Number(value || 0).toLocaleString('el-GR', { maximumFractionDigits: 0 })
            },
            formatDay(value) {
                return moment(value).tz(ZONE).format('ddd D MMM')
            },
            formatTime(value) {
                return moment(value).tz(ZONE).format('HH:mm')
            },
            // remembered per browser: period, grouping and filters
            loadState() {
                try {
                    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
                    if (!saved) return
                    if (this.periodOptions.some(option => option.value === saved.period)) this.period = saved.period
                    if (this.groupUnits.some(unit => unit.value === saved.groupByChoice)) this.groupByChoice = saved.groupByChoice
                    this.customFrom = saved.customFrom || null
                    this.customTo = saved.customTo || null
                    this.filters = { ...this.filters, ...(saved.filters || {}) }
                } catch (e) {
                    // nothing saved or storage unavailable: defaults
                }
            },
            saveState() {
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify({
                        period: this.period,
                        groupByChoice: this.groupByChoice,
                        customFrom: this.customFrom,
                        customTo: this.customTo,
                        filters: this.filters
                    }))
                } catch (e) {
                    // storage unavailable: the choice lasts for this visit
                }
            }
        }
    }
</script>

<style scoped>
.welcome {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.welcome-message {
    font-weight: bold;
    font-size: calc(1vw + 2vh);
}

.dash-card {
    padding: 16px 18px;
    border-radius: 10px !important;
    height: 100%;
}

.dash-card__title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 16px;
    font-weight: 700;
    font-size: 15px;
    margin-bottom: 12px;
}

.dash-filters {
    height: auto;
}

.dash-filters__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.dash-filters__group {
    display: flex;
    align-items: center;
}

.dash-filters__range {
    margin-top: 10px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
}

.dash-filters__range a {
    color: #00695c;
}

.dash-chip--active {
    background: #e0f2f1 !important;
    border-color: #009688 !important;
    color: #00695c !important;
    font-weight: 600;
}

.dash-notes {
    margin-top: 12px;
}

.dash-note {
    display: flex;
    align-items: flex-start;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.7);
    padding: 6px 2px;
}

.dash-subs {
    display: grid;
    grid-template-columns: minmax(220px, 300px) 1fr;
    gap: 24px;
    align-items: start;
}

.dash-subs__count {
    margin-bottom: 8px;
    font-size: 1.05rem;
}

.dash-subs__count b {
    font-size: 1.6rem;
}

.dash-subs__label {
    margin-bottom: 6px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
}

.dash-subs__collected {
    margin-left: auto;
    color: #2e7d32;
    font-size: 0.85rem;
    font-weight: 700;
}

@media (max-width: 700px) {
    .dash-subs {
        grid-template-columns: 1fr;
    }
}

.dash-kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    margin-top: 16px;
}

.dash-kpi {
    background: #fff;
    border-radius: 10px;
    padding: 14px 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    min-width: 0;
}

.dash-kpi__label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.55);
}

.dash-kpi__label .v-icon {
    color: rgba(0, 0, 0, 0.45);
}

.dash-kpi__value {
    font-size: clamp(20px, 5vw, 28px);
    font-weight: 700;
    line-height: 1.3;
    margin-top: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.dash-kpi__sub {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.55);
}

.dash-split {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 14px;
    margin: 8px 0 6px;
    font-size: 14px;
}

.dash-split__item {
    display: flex;
    align-items: center;
    gap: 5px;
}

.dash-positive {
    color: #2e7d32;
}

.dash-negative {
    color: #c62828;
}

.dash-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-left: auto;
    font-size: 12px;
    font-weight: 400;
    text-transform: none;
    color: rgba(0, 0, 0, 0.6);
}

.dash-legend > span {
    display: flex;
    align-items: center;
    gap: 5px;
}

.bar-chart-dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 2px;
}

.dash-empty {
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
    padding: 8px 0;
}

.dash-upcoming {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 6px;
    border-radius: 8px;
    cursor: pointer;
}

.dash-upcoming:hover {
    background: #f5f5f5;
}

.dash-upcoming__date {
    display: flex;
    flex-direction: column;
    width: 76px;
    flex: none;
    font-size: 13px;
}

.dash-upcoming__date span {
    color: rgba(0, 0, 0, 0.55);
    font-size: 12px;
}

.dash-upcoming__teams {
    flex: 1;
    min-width: 0;
    font-size: 14px;
}

.dash-counters {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
}

.dash-counters b {
    color: rgba(0, 0, 0, 0.87);
    font-size: 15px;
}

.dash-counters__link {
    cursor: pointer;
}

.dash-loading {
    display: flex;
    justify-content: center;
    padding: 60px 0;
}
</style>
